<template>
  <div id="overview" class="margin-subpages">
    <!-- ============================ Happily Houses ============================ -->
    <div id="happyHouses">
      <div class="hh-head">
        <div class="sub-title">{{ $t("happilyVillage.HAPPILY_HOUSES") }}</div>
        <!-- popper-class on <el-dropdown> is silently ignored in Element UI
             2.15.9 (no popperClass prop); the class must be merged onto the
             menu's root <ul> instead, so it goes on <el-dropdown-menu>. -->
        <el-dropdown
          trigger="click"
          placement="bottom-end"
          @command="onRangeCommand"
        >
          <span class="header-pill-dropdown" :class="{ active: !isDefaultRange }">
            {{ rangeLabel }} <i class="el-icon-arrow-down"></i>
          </span>
          <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper">
            <el-dropdown-item
              v-for="opt in rangeOptions"
              :key="opt.value"
              :command="opt.value"
              :class="{ 'hh-range-selected': opt.value === range }"
            >
              {{ opt.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div class="line-style mt-16 mb-20" />

      <!-- Column legend: the three metric headers carry the color coding so the
           rows themselves stay neutral and scannable. -->
      <div class="hh-board-head">
        <div></div>
        <div class="hh-col xp">{{ $t("happilyVillage.House_XP") }}</div>
        <div class="hh-col distance">{{ $t("happilyVillage.Distance") }}</div>
        <div class="hh-col entries">{{ $t("happilyVillage.Mission_Entries") }}</div>
      </div>

      <!-- Loaded leaderboard -->
      <div v-if="houses.length" class="hh-board">
        <div v-for="(house, index) in houses" :key="house.key" class="hh-row">
          <!-- House identity: crest carries the rank badge so the row leads
               with one tidy mark instead of a medal + a separate crest. -->
          <div class="hh-house">
            <div class="hh-crest-wrap">
              <img
                class="hh-crest"
                :src="houseImage(house)"
                :alt="house.house_name"
              />
              <span class="hh-rank-badge" :class="rankClass(index + 1)">{{
                index + 1
              }}</span>
            </div>
            <div class="hh-identity">
              <div class="hh-name">{{ house.house_name }}</div>
              <div class="hh-meta">
                <span class="hh-level"
                  >{{ $t("happilyVillage.Level") }} {{ house.level }}</span
                >
                <span class="hh-meta-sep"></span>
                <span class="hh-active">
                  <span class="hh-active-n">{{
                    $utils("addCommasInNumber", renderMembers(house.key))
                  }}</span>
                  active
                </span>
              </div>
              <button
                type="button"
                class="hh-total"
                @click="showingMembersOfHouse(house)"
              >
                {{ $utils("addCommasInNumber", house.members) }}
                {{ $t("happilyVillage.total_members") }}
              </button>
            </div>
          </div>

          <!-- Stats (per house) -->
          <template v-if="statsReady">
            <div class="hh-cell">
              <div class="hh-cell-value">
                +{{ $utils("addCommasInNumber", renderXP(house.key)) }}
                <span class="hh-cell-unit">XP</span>
              </div>
              <div class="hh-cell-sub">
                +{{
                  $utils(
                    "addCommasInNumber",
                    renderXPPerPerson(house.key, house.members)
                  )
                }}
                / {{ $t("happilyVillage.person") }}
              </div>
            </div>
            <div class="hh-cell">
              <div class="hh-cell-value">
                +{{ $utils("addCommasInNumber", renderDistance(house.key)) }}
                <span class="hh-cell-unit">{{ $t("happilyVillage.km") }}</span>
              </div>
              <div class="hh-cell-sub">
                +{{
                  $utils(
                    "addCommasInNumber",
                    renderDistancePerPerson(house.key, house.members)
                  )
                }}
                {{ $t("happilyVillage.km_per_person") }}
              </div>
            </div>
            <div class="hh-cell">
              <div class="hh-cell-value">
                +{{ $utils("addCommasInNumber", renderEntry(house.key)) }}
                <span class="hh-cell-unit">{{
                  $t("happilyVillage.entries")
                }}</span>
              </div>
              <div class="hh-cell-sub">
                {{ $utils("addCommasInNumber", renderParticipation(house.key)) }}
                {{ $t("happilyVillage.participation") }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="hh-cell" v-for="n in 3" :key="`s${n}`">
              <div class="text-skeleton hh-skel" style="width: 70%"></div>
              <div class="text-skeleton hh-skel short" style="width: 50%"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- Skeleton leaderboard -->
      <div v-else class="hh-board">
        <div v-for="n in 3" :key="`row${n}`" class="hh-row">
          <div class="hh-house">
            <div class="hh-crest-wrap">
              <div class="hh-crest skeleton-block"></div>
            </div>
            <div class="hh-identity">
              <div class="text-skeleton hh-skel" style="width: 120px"></div>
              <div
                class="text-skeleton hh-skel short"
                style="width: 90px; margin-top: 8px"
              ></div>
              <div
                class="text-skeleton hh-skel short"
                style="width: 140px; margin-top: 8px"
              ></div>
            </div>
          </div>
          <div class="hh-cell" v-for="m in 3" :key="`rs${m}`">
            <div class="text-skeleton hh-skel" style="width: 70%"></div>
            <div class="text-skeleton hh-skel short" style="width: 50%"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================ Pillar charts ============================ -->
    <div class="hv-ov-grid">
      <!-- University -->
      <div id="university" class="hv-ov-card hv-chart-card">
        <div class="sub-title hv-ov-section">
          {{ $t("happilyVillage.university") }}
        </div>
        <div class="line-style mt-12 mb-16" />
        <div class="hv-ov-head">
          <div class="hv-ov-stat">
            <span class="hv-ov-num">{{
              $utils("addCommasInNumber", university_total)
            }}</span>
            <span class="hv-ov-cap">{{
              $t("happilyVillage.Learning_and_Activity")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="hv-ov-tip" slot="content">
              {{ $t("happilyVillage.A_count_of_how_many_learning") }}
            </div>
            <span class="hv-ov-help">
              <img :src="getImgUrl('help_outline-16px')" alt="" />
            </span>
          </el-tooltip>
        </div>
        <div class="hv-ov-plot">
          <div v-if="universityChartStatus" class="absolute-mid">
            <loading-even></loading-even>
          </div>
          <canvas ref="universityBarchart" class="barchartStyle"></canvas>
        </div>
        <div class="hv-ov-foot">
          <span class="see-more" @click="goTo('university')">
            {{ $t("happilyVillage.See_more") }}
            <i class="el-icon-arrow-right"></i>
          </span>
        </div>
      </div>

      <!-- Town Hall -->
      <div id="townHall" class="hv-ov-card hv-chart-card">
        <div class="sub-title hv-ov-section">
          {{ $t("happilyVillage.town_hall") }}
        </div>
        <div class="line-style mt-12 mb-16" />
        <div class="hv-ov-head">
          <div class="hv-ov-stat">
            <span class="hv-ov-num">{{
              $utils("addCommasInNumber", townhall_total)
            }}</span>
            <span class="hv-ov-cap">{{
              $t("happilyVillage.Knowledge_Sharing_Interactions")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="hv-ov-tip" slot="content">
              {{ $t("happilyVillage.A_count_of_how_many_comments") }}
            </div>
            <span class="hv-ov-help">
              <img :src="getImgUrl('help_outline-16px')" alt="" />
            </span>
          </el-tooltip>
        </div>
        <div class="hv-ov-plot">
          <div v-if="townHallChartStatus" class="absolute-mid">
            <loading-even></loading-even>
          </div>
          <canvas ref="townHallBarchart" class="barchartStyle"></canvas>
        </div>
        <div class="hv-ov-foot">
          <span class="see-more" @click="goTo('townHall')">
            {{ $t("happilyVillage.See_more") }}
            <i class="el-icon-arrow-right"></i>
          </span>
        </div>
      </div>

      <!-- Running Track -->
      <div id="runningTrack" class="hv-ov-card hv-chart-card">
        <div class="sub-title hv-ov-section">
          {{ $t("happilyVillage.running_track") }}
        </div>
        <div class="line-style mt-12 mb-16" />
        <div class="hv-ov-head">
          <div class="hv-ov-stat">
            <span class="hv-ov-num">{{
              $utils("addCommasInNumber", distance_all)
            }}</span>
            <span class="hv-ov-cap">{{
              $t("happilyVillage.km_Distance_Traveled")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="hv-ov-tip" slot="content">
              {{ $t("happilyVillage.A_count_of_distance_traveled") }}
            </div>
            <span class="hv-ov-help">
              <img :src="getImgUrl('help_outline-16px')" alt="" />
            </span>
          </el-tooltip>
        </div>
        <div class="hv-ov-plot">
          <div v-if="runningTrackChartStatus" class="absolute-mid">
            <loading-even></loading-even>
          </div>
          <canvas ref="runningTrackBarchart" class="barchartStyle"></canvas>
        </div>
        <div class="hv-ov-foot">
          <span class="see-more" @click="goTo('runningTrack')">
            {{ $t("happilyVillage.See_more") }}
            <i class="el-icon-arrow-right"></i>
          </span>
        </div>
      </div>

      <!-- Battle Stadium -->
      <div id="battleStadium" class="hv-ov-card hv-chart-card">
        <div class="sub-title hv-ov-section">
          {{ $t("happilyVillage.BATTLE_STADIUM") }}
        </div>
        <div class="line-style mt-12 mb-16" />
        <div class="hv-ov-head">
          <div class="hv-ov-stat">
            <span class="hv-ov-num">{{
              $utils("addCommasInNumber", missionEntry)
            }}</span>
            <span class="hv-ov-cap">{{
              $t("happilyVillage.Mission_Entries")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="hv-ov-tip" slot="content">
              {{ $t("happilyVillage.A_count_of_how_many_entries") }}
            </div>
            <span class="hv-ov-help">
              <img :src="getImgUrl('help_outline-16px')" alt="" />
            </span>
          </el-tooltip>
        </div>
        <div class="hv-ov-plot">
          <div v-if="battleStadiumChartStatus" class="absolute-mid">
            <loading-even></loading-even>
          </div>
          <canvas ref="battleStadiumchart" class="barchartStyle"></canvas>
        </div>
        <div class="hv-ov-foot">
          <span class="see-more" @click="goTo('battleStadium')">
            {{ $t("happilyVillage.See_more") }}
            <i class="el-icon-arrow-right"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- ============================ House members ============================ -->
    <el-dialog
      :visible.sync="dialogHouseMembers"
      top="8vh"
      custom-class="hh-members-dialog"
    >
      <div class="hh-members-head">
        <div class="qd-eyebrow">
          {{ $t("happilyVillage.House_of") }} {{ dialogHouseMembersTitle }}
        </div>
        <div class="hh-members-count">
          {{ $utils("addCommasInNumber", members.length) }}
          {{ $t("happilyVillage.members") }}
        </div>
        <div class="hh-members-sub">
          {{ $t("happilyVillage.making_up") }}
          {{ $utils("addCommasInNumber", population) }}%
          {{ $t("happilyVillage.of_Happily_Village") }}
        </div>
      </div>
      <div class="members-recipients thin-scrollbar">
        <div v-for="item in members" :key="item.email" class="rowDataStyle">
          <div class="columnDataStyle" style="width: 100%">
            <div class="flex-center py-7">
              <div v-if="item.image">
                <img
                  v-lazy="item.image"
                  :key="item.image"
                  class="image-skeleton image-profile-42 flex-mid mr-16"
                  :class="[blurMode ? 'blurelement' : '']"
                />
              </div>
              <div
                v-else
                class="image image-profile-42 flex-mid mr-12"
                :class="[blurMode ? 'blurelement' : '']"
              >
                <div
                  class="flex-mid text-in-image text-in-image-42"
                  style="width: 42px"
                >
                  {{
                    $utils("acronymName", {
                      firstName: item.firstname,
                      lastName: item.lastname,
                    })
                  }}
                </div>
              </div>
              <div class="flex-column pl-4">
                <span
                  class="text-name"
                  :class="[blurMode ? 'blurelement' : '']"
                  style="width: 200px"
                  >{{ item.firstname }} {{ item.lastname }}</span
                >
                <span
                  class="text-email"
                  :class="[blurMode ? 'blurelement' : '']"
                  style="width: 200px"
                  >{{ item.email }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { buildHVBarOptions, buildHVBarDataset } from "./helpers/chart-defaults.js";
Chart.defaults.set('plugins.datalabels', {
  anchor: "end",
  align: "end",
  clamp: true,
  offset: 0,
  display: function(context) {
    return context.dataset.data[context.dataIndex] != 0;
  },
});
import _ from "lodash";

export default {
  name: "overview",
  components: {
    loadingEven: require("../even/loading").default,
  },
  created() {
    this.$store.dispatch("happilyvillage/getDataHouse");
    this.universityChartStatus = true;
    this.townHallChartStatus = true;
    this.runningTrackChartStatus = true;
    this.battleStadiumChartStatus = true;
    this.$store.dispatch("happilyvillageUniversity/getUniversity").then(() => {
      this.randerBarChartUniversity();
    });
    this.$store.dispatch("happilyvillageTownhall/getTownhall").then(() => {
      this.randerBarChartTownhall();
    });
    this.$store.dispatch("happilyvillage/getDataRunningTrack").then(() => {
      this.randerBarChartRunnigtrack();
    });
    this.$store
      .dispatch("happilyvillageBattleStadium/getMisiionUser")
      .then(() => {
        this.randerBarChartBattleStadium();
      });
  },
  computed: {
    activeUsers() {
      return this.$store.getters["happilyvillage/activeUsers"];
    },
    houses() {
      return this.$store.getters["happilyvillage/houses"];
    },
    housesfilter() {
      return this.$store.getters["happilyvillage/housesfilter"];
    },
    // The per-house stat columns depend on the windowed filter payload. Until
    // it arrives we show skeletons in the stat cells (house identity is shown
    // immediately so the leaderboard never looks empty).
    statsReady() {
      return !!(this.housesfilter && Object.keys(this.housesfilter).length);
    },
    rangeOptions() {
      return [
        { value: "THIS WEEK", label: this.$t("happilyVillage.This_Week") },
        { value: "THIS MONTH", label: this.$t("happilyVillage.This_Month") },
        { value: "LAST WEEK", label: this.$t("happilyVillage.Last_Week") },
        { value: "LAST MONTH", label: this.$t("happilyVillage.Last_Month") },
        { value: "ALL-TIME", label: this.$t("happilyVillage.All_time") },
      ];
    },
    rangeLabel() {
      const match = this.rangeOptions.find((o) => o.value === this.range);
      return match ? match.label : this.range;
    },
    isDefaultRange() {
      return this.range === "THIS WEEK";
    },
    university() {
      return this.$store.getters["happilyvillageUniversity/university"];
    },
    university_graphmonth() {
      return this.$store.getters[
        "happilyvillageUniversity/university_graphmonth"
      ];
    },
    university_total() {
      return this.$store.getters["happilyvillageUniversity/university_total"];
    },
    townhall_graphmonth() {
      return this.$store.getters["happilyvillageTownhall/townhall_graphmonth"];
    },
    townhall_total() {
      return this.$store.getters["happilyvillageTownhall/townhall_total"];
    },

    distance_all() {
      return this.$store.getters["happilyvillage/distance_all"];
    },
    runningtrack_graphmonth() {
      return this.$store.getters["happilyvillage/runningtrack_graphmonth"];
    },
    members() {
      return this.$store.getters["happilyvillage/members"];
    },
    battleStadium_graphmonth() {
      return this.$store.getters["happilyvillageBattleStadium/missionChart6"];
    },
    missionEntry() {
      return this.$store.getters["happilyvillageBattleStadium/missionEntry"];
    },
    mission() {
      return this.$store.getters["happilyvillage/getMission"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
  },
  data() {
    return {
      range: "THIS WEEK",
      dialogHouseMembers: false,
      dialogHouseMembersTitle: null,
      universityChartStatus: false,
      townHallChartStatus: false,
      runningTrackChartStatus: false,
      battleStadiumChartStatus: false,
      population: 0,
    };
  },
  methods: {
    renderXPPerPerson(key, members) {
      if (
        this.housesfilter &&
        this.housesfilter[key] !== undefined &&
        this.housesfilter[key].xp !== undefined &&
        members
      ) {
        return (this.housesfilter[key].xp / members).toFixed(2);
      }
      return 0;
    },
    renderXP(key) {
      if (
        this.housesfilter &&
        this.housesfilter[key] !== undefined &&
        this.housesfilter[key].xp !== undefined
      ) {
        return this.housesfilter[key].xp;
      }
      return 0;
    },
    renderDistancePerPerson(key, members) {
      if (
        this.housesfilter &&
        this.housesfilter[key] !== undefined &&
        this.housesfilter[key].distance &&
        members
      ) {
        return (this.housesfilter[key].distance / 1000 / members).toFixed(2);
      }
      return 0;
    },
    renderDistance(key) {
      if (
        this.housesfilter &&
        this.housesfilter[key] !== undefined &&
        this.housesfilter[key].distance
      ) {
        return (this.housesfilter[key].distance / 1000).toFixed(2);
      }
      return 0;
    },
    renderMembers(key) {
      if (this.activeUsers && this.activeUsers[key] !== undefined) {
        return this.activeUsers[key];
      }
      return 0;
    },
    renderEntry(key) {
      if (this.mission && this.mission[key] !== undefined) {
        return this.mission[key].entry;
      }
      return 0;
    },
    renderParticipation(key) {
      if (this.mission && this.mission[key] !== undefined) {
        return this.mission[key].participation;
      }
      return 0;
    },
    // Top-3 medal treatment, matching the Running Track leaderboard.
    rankClass(rank) {
      if (rank === 1) return "rank-gold";
      if (rank === 2) return "rank-silver";
      if (rank === 3) return "rank-bronze";
      return "";
    },
    houseImage(house) {
      const images = require.context(
        "../../assets/images/happilyvillage/",
        false,
        /\.png$/
      );
      const map = {
        Delphinus: "Delphinus_32@2x.png",
        Corvus: "Corvus_32@2x.png",
      };
      const file = map[house.house_name] || "Leonos_32@2x.png";
      return images("./" + file);
    },
    tableHouses(value) {
      this.$store.dispatch("happilyvillage/getDataHouseFilter", value);
    },
    onRangeCommand(value) {
      this.range = value;
      this.tableHouses(value);
    },
    randerBarChartUniversity() {
      var universityBarchart = this.$refs.universityBarchart;
      const { university_graphmonth } = this;
      const month = [];
      const value = [];
      for (const x in university_graphmonth) {
        month.push(university_graphmonth[x].month);
        value.push(university_graphmonth[x].value);
      }
      // Reverse the arrays to show oldest months first
      month.reverse();
      value.reverse();

      if (universityBarchart) {
        this.universityChartStatus = false;
        const universityBarChartData = {
          labels: month,
          datasets: [
            {
              label: "Learning Interactions",
              data: value,
              ...buildHVBarDataset('university'),
            },
          ],
        };
        this.universityBarchart = new Chart(
          universityBarchart.getContext("2d"),
          {
            plugins: [ChartDataLabels],
            type: "bar",
            data: universityBarChartData,
            options: buildHVBarOptions('university'),
          }
        );
      }
    },
    randerBarChartTownhall() {
      var townHallBarchart = this.$refs.townHallBarchart;
      const { townhall_graphmonth } = this;
      const month = [];
      const value = [];
      for (const x in townhall_graphmonth) {
        month.push(townhall_graphmonth[x].month);
        value.push(townhall_graphmonth[x].value);
      }
      // Reverse the arrays to show oldest months first
      month.reverse();
      value.reverse();

      if (townHallBarchart) {
        this.townHallChartStatus = false;
        const townHallBarChartData = {
          labels: month,
          datasets: [
            {
              label: "Learning Interactions",
              data: value,
              ...buildHVBarDataset('townhall'),
            },
          ],
        };
        this.townHallBarchart = new Chart(townHallBarchart.getContext("2d"), {
          plugins: [ChartDataLabels],
          type: "bar",
          data: townHallBarChartData,
          options: buildHVBarOptions('townhall'),
        });
      }
    },
    randerBarChartRunnigtrack() {
      var runningTrackBarchart = this.$refs.runningTrackBarchart;
      const { runningtrack_graphmonth } = this;
      const month = [];
      const value = [];
      for (const x in runningtrack_graphmonth) {
        month.push(runningtrack_graphmonth[x].month);
        value.push(runningtrack_graphmonth[x].value);
      }
      // Reverse the arrays to show oldest months first
      month.reverse();
      value.reverse();

      if (runningTrackBarchart) {
        this.runningTrackChartStatus = false;
        const runningTarckBarChartData = {
          labels: month,
          datasets: [
            {
              label: "Learning Interactions",
              data: value,
              ...buildHVBarDataset('runningtrack'),
            },
          ],
        };
        this.runningTrackBarchart = new Chart(
          runningTrackBarchart.getContext("2d"),
          {
            plugins: [ChartDataLabels],
            type: "bar",
            data: runningTarckBarChartData,
            options: buildHVBarOptions('runningtrack', {
              datalabelFormatter: function(value) {
                return value != 0 ? value : '';
              },
            }),
          }
        );
      }
    },
    randerBarChartBattleStadium() {
      var battleStadiumchart = this.$refs.battleStadiumchart;
      const month = [];
      const value = [];
      for (const x in this.battleStadium_graphmonth) {
        month.push(this.battleStadium_graphmonth[x].month);
        value.push(this.battleStadium_graphmonth[x].value);
      }
      // Reverse the arrays to show oldest months first
      month.reverse();
      value.reverse();

      if (battleStadiumchart) {
        this.battleStadiumChartStatus = false;
        const battleStadiumBarChartData = {
          labels: month,
          datasets: [
            {
              label: "",
              data: value,
              ...buildHVBarDataset('battle'),
            },
          ],
        };
        this.battleStadiumBarChart = new Chart(
          battleStadiumchart.getContext("2d"),
          {
            plugins: [ChartDataLabels],
            type: "bar",
            data: battleStadiumBarChartData,
            options: buildHVBarOptions('battle', {
              datalabelFormatter: function(value) {
                return value != 0 ? value : '';
              },
            }),
          }
        );
      }
    },
    getImgUrl(pic) {
      var images = require.context("../../assets/images/", false, /\.png$/);
      return images("./" + pic + ".png");
    },
    showingMembersOfHouse(house) {
      this.dialogHouseMembers = true;
      this.dialogHouseMembersTitle = house.house_name;
      this.population = house.population;
      this.$store.dispatch("happilyvillage/getDataHouseMember", house.key);
    },
    goTo(tab) {
      this.$emit("tabChanged", tab);
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #6c30cc;
$ink: #1a1a1a;
$body: #4a4a4a;
$muted: #8a8a8a;
$faint: #9a9a9a;
$line: #eef0f5;

// Metric accent colors — deepened from the playful chart fills so they stay
// legible as small uppercase labels on white.
$xp: #0f9d6e;
$distance: #d97411;
$entries: #e11059;

/* ============================ Section header ============================ */
.hh-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ============================ Houses leaderboard ============================ */
// Shared grid keeps the column legend and every row in lock-step alignment.
// The house column flexes; the three metric columns are fixed so the values
// sit in tight, scannable columns rather than drifting across the full width.
.hh-board-head,
.hh-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 150px 160px 172px;
  column-gap: 20px;
  align-items: center;
}

.hh-board-head {
  padding: 0 12px 12px;
}
.hh-col {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.hh-col.xp {
  color: $xp;
}
.hh-col.distance {
  color: $distance;
}
.hh-col.entries {
  color: $entries;
}

// Hairline dividers (matching the Running Track table) help the eye track a
// single house across the wide row; hover lifts the whole row.
.hh-row {
  padding: 16px 12px;
  transition: background-color 0.16s ease;
}
.hh-row + .hh-row {
  border-top: 1px solid #f1f2f5;
}
.hh-row:hover {
  background: #faf9ff;
}

/* ----- House identity cell ----- */
.hh-house {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.hh-crest-wrap {
  position: relative;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
}
.hh-crest {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: block;
}
// Rank rides the crest corner as a small medal chip — gold/silver/bronze for
// the podium, neutral otherwise.
.hh-rank-badge {
  position: absolute;
  top: -7px;
  left: -7px;
  min-width: 21px;
  height: 21px;
  padding: 0 5px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  background: #b9bcc6;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.hh-rank-badge.rank-gold {
  background: linear-gradient(135deg, #ffd668 0%, #f5b800 100%);
  color: #7a4f00;
}
.hh-rank-badge.rank-silver {
  background: linear-gradient(135deg, #e6e9ee 0%, #aab1bd 100%);
  color: #4a5160;
}
.hh-rank-badge.rank-bronze {
  background: linear-gradient(135deg, #f0b486 0%, #c87533 100%);
  color: #4a2708;
}

.hh-identity {
  min-width: 0;
}
.hh-name {
  font-size: 16px;
  font-weight: 800;
  color: $ink;
  letter-spacing: -0.01em;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hh-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 13px;
  color: $faint;
}
.hh-meta-sep {
  flex: 0 0 auto;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #d0d2da;
}
.hh-level {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f3f4f7;
  color: #626262;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.hh-active-n {
  color: $ink;
  font-weight: 700;
}
.hh-total {
  display: inline-block;
  margin-top: 5px;
  border: none;
  background: none;
  padding: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: $muted;
  cursor: pointer;
  transition: color 0.16s ease;
  font-family: inherit;
}
.hh-total:hover {
  color: $primary;
  text-decoration: underline;
}

/* ----- Stat cells ----- */
.hh-cell {
  min-width: 0;
}
.hh-cell-value {
  font-size: 16px;
  font-weight: 800;
  color: $ink;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  white-space: nowrap;
}
.hh-cell-unit {
  font-size: 11px;
  font-weight: 700;
  color: $faint;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 1px;
}
.hh-cell-sub {
  margin-top: 4px;
  font-size: 12px;
  color: $faint;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ----- Skeletons ----- */
.hh-skel {
  height: 13px;
  border-radius: 6px;
}
.hh-skel.short {
  height: 11px;
  margin-top: 6px;
}
.skeleton-block {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #eef0f5;
}

/* ============================ Pillar chart cards ============================ */
.hv-ov-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 32px;
}
.hv-ov-card {
  display: flex;
  flex-direction: column;
  padding: 20px 20px 14px;
}
.hv-ov-section {
  // The shared `.sub-title` is 18px/700 uppercase; trim it slightly for the
  // denser 2-up card grid.
  font-size: 15px;
}
.hv-ov-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  // Reserve room for a two-line caption so every card's header is the same
  // height regardless of label length — keeps the four charts aligned.
  min-height: 58px;
  margin-bottom: 6px;
}
.hv-ov-stat {
  min-width: 0;
}
.hv-ov-num {
  display: block;
  font-size: 24px;
  font-weight: 900;
  color: $ink;
  line-height: 1.12;
  letter-spacing: -0.01em;
  margin-bottom: 2px;
}
.hv-ov-cap {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #626262;
  line-height: 1.4;
}
.hv-ov-help {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  opacity: 0.5;
  cursor: help;
  transition: opacity 0.18s ease, background-color 0.18s ease;

  img {
    width: 16px;
    height: 16px;
    display: block;
  }
}
.hv-ov-help:hover {
  opacity: 0.85;
  background: rgba(108, 48, 204, 0.08);
}
.hv-ov-tip {
  width: 230px;
  line-height: 1.5;
}

// Fixed-height plot box gives Chart.js (maintainAspectRatio:false) a definite
// height to fill, so all four mini-charts render at the exact same height.
.hv-ov-plot {
  position: relative;
  height: 150px;
}
.barchartStyle {
  border-radius: 4px;
}

.hv-ov-foot {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
.see-more {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: $primary;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.18s ease;

  i {
    font-size: 12px;
    font-weight: 700;
    transition: transform 0.18s ease;
  }
}
.see-more:hover {
  color: #522698;

  i {
    transform: translateX(2px);
  }
}

/* ============================ House members dialog ============================ */
.hh-members-head {
  padding: 0 0 16px;
  border-bottom: 1px solid $line;
  margin-bottom: 4px;
}
.hh-members-count {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 800;
  color: $ink;
  letter-spacing: -0.01em;
}
.hh-members-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #626262;
}
.members-recipients {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 4px 0 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 55vh;
}
</style>

<style>
#overview .el-dialog__body {
  padding-bottom: 8px;
}
/* Selected range — global (not scoped) because the dropdown popper teleports to
   <body>; pairs with the shared `happily-dropdown-popper` hover styling. */
.happily-dropdown-popper .hh-range-selected {
  color: #6c30cc;
  font-weight: 700;
}
</style>
